/*
 * Scroll story / Shop the story
 * <scroll-story>: sticky media column + steps column; the step whose centre is nearest the frame centre is active.
 * Mobile (<= 1023px): the layers sit inline above their steps (CSS only) and the reference line is the viewport centre.
 * Lazy-init class (Motion.inView in the constructor, `initialized` latch + re-arm in connectedCallback) — see ARCHITECTURE.md.
 */
if (!customElements.get('scroll-story')) {
  const escapeHtml = (value) => String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

  class ScrollStory extends BaseElement {
    constructor() {
      super();

      Motion.inView(this, this.init.bind(this), { margin: '400px 0px 400px 0px' });
    }

    connectedCallback() {
      super.connectedCallback();

      if (window.Shopify?.designMode) {
        const section = this.closest('.shopify-section');
        this.on(this, 'shopify:block:select', this.onBlockSelect.bind(this));
        this.on(this, 'shopify:block:deselect', this.onBlockDeselect.bind(this));
        if (section) {
          // Skip the re-fire after a section re-render (detail.load), like theme.js's other section:select handlers;
          // on a real selection recompute from the scroll position (the editor scrolls the section into view itself).
          this.on(section, 'shopify:section:select', (event) => {
            if (!this.initialized || event.detail?.load) return;
            this.pinned = null;
            clearTimeout(this.pinTimer);
            this.update();
          });
        }
      }

      // Re-arm after a DOM move (theme editor reorder) or a section re-render.
      if (this.initialized) {
        this.initialized = false;
        this.init();
      }
    }

    init() {
      if (this.initialized) return;
      this.initialized = true;

      this.media = this.querySelector('[data-media]');
      this.frames = Array.from(this.media?.querySelectorAll('[data-frame]') ?? []);
      this.lastFrame = this.media?.querySelector('[data-last-frame]') ?? null;
      this.steps = Array.from(this.querySelectorAll('[data-step]'));
      this.column = this.querySelector('[data-steps]');
      this.bframe = this.querySelector('[data-bframe]');
      this.current = -1;
      this.pinned = null;
      this.mql = window.matchMedia('(max-width: 1023px)');

      if (!this.media || this.steps.length === 0) return;

      const onScroll = theme.utils.throttle(this.update.bind(this));
      this.on(window, 'scroll', onScroll, { passive: true });
      this.on(window, 'resize', onScroll, { passive: true });
      // Crossing the breakpoint (theme editor device preview): re-run activate() so the layer attributes match the layout.
      this.on(this.mql, 'change', () => {
        this.current = -1;
        this.update();
      });
      this.registerCleanup(() => {
        onScroll.cancel();
        clearTimeout(this.pinTimer);
      });

      // Keyboard users: focusing a control inside a step shows that step's image.
      this.on(this, 'focusin', this.onFocusIn.bind(this));

      // Variant pickers (buy panel here; steps too in Shop the story).
      this.on(this, 'change', this.onChange.bind(this));

      this.update();
    }

    /**
     * The media layer that belongs to a step: layers are keyed by block id, the buy panel uses the last layer.
     */
    frameFor(index) {
      const step = this.steps[index];
      if (!step) return null;
      if (step.hasAttribute('data-buy')) return this.lastFrame;
      return this.frames.find((frame) => frame.dataset.stepId === step.dataset.stepId) ?? null;
    }

    /**
     * Active step = the step whose vertical centre is nearest the reference line.
     * Desktop: the sticky frame centre. Mobile: the viewport centre (the layers are inline, nothing is pinned).
     * The buy panel wins once its top passes the reference line or (desktop) its bottom reaches the frame bottom.
     */
    update() {
      if (this.pinned !== null || !this.media || this.steps.length === 0) return;

      const mobile = this.mql.matches;
      const frame = this.media.getBoundingClientRect();
      const reference = mobile ? window.innerHeight / 2 : frame.top + frame.height / 2;

      let best = 0;
      let bestDistance = Infinity;
      this.steps.forEach((step, index) => {
        const rect = step.getBoundingClientRect();
        const distance = Math.abs(rect.top + rect.height / 2 - reference);
        if (distance < bestDistance) {
          bestDistance = distance;
          best = index;
        }
      });

      // Tail rule: once the sticky frame is clamped at the end of the steps column (column bottom == frame bottom,
      // 1px for sub-pixel rounding) the last step wins, even if there is no room left to scroll its centre to the
      // reference line. With a buy panel, the panel also wins as soon as its top passes the reference line (prototype rule).
      const last = this.steps.length - 1;
      if (last > 0) {
        // Mobile: the media and steps wrappers have no box (display: contents), and nothing is clamped anyway.
        const columnBottom = (this.column ?? this.steps[last]).getBoundingClientRect().bottom;
        const pushed = !mobile && columnBottom <= frame.bottom + 1;
        const panelPassed = this.bframe ? this.bframe.getBoundingClientRect().top < reference : false;
        if (pushed || panelPassed) best = last;
      }

      this.activate(best);
    }

    activate(index) {
      if (index < 0 || index >= this.steps.length || index === this.current) return;
      this.current = index;

      const activeFrame = this.frameFor(index);
      const mobile = this.mql.matches;

      this.steps.forEach((step, i) => {
        const active = i === index || step.hasAttribute('data-buy');
        step.classList.toggle('is-active', active);
        if (i === index) {
          step.setAttribute('aria-current', 'true');
        }
        else {
          step.removeAttribute('aria-current');
        }
      });

      // Desktop: only the active layer is exposed. Mobile: every layer is visible inline, so none is hidden.
      this.frames.forEach((frame) => {
        const active = frame === activeFrame;
        frame.classList.toggle('is-active', active);
        if (active || mobile) {
          frame.removeAttribute('aria-hidden');
        }
        else {
          frame.setAttribute('aria-hidden', 'true');
        }
      });

      this.dispatchEvent(new CustomEvent('scroll-story:active', { bubbles: true, detail: { index, step: this.steps[index] } }));
    }

    /**
     * Force a step active and ignore scroll-driven updates for a moment
     * (theme editor selection, keyboard focus → the browser's own focus-scroll would otherwise flip it back).
     */
    pin(index, duration = 1000) {
      if (index < 0 || index >= this.steps.length) return;

      this.pinned = index;
      this.activate(index);

      clearTimeout(this.pinTimer);
      this.pinTimer = setTimeout(() => {
        this.pinned = null;
      }, duration);
    }

    onFocusIn(event) {
      const step = event.target.closest?.('[data-step]');
      if (step) this.pin(this.steps.indexOf(step));
    }

    onBlockSelect(event) {
      const step = event.target.closest('[data-step]');
      if (step && this.initialized) this.pin(this.steps.indexOf(step), 1500);
    }

    onBlockDeselect() {
      this.pinned = null;
      clearTimeout(this.pinTimer);
      if (this.initialized) this.update();
    }

    /* ---- variants (buy panel) ---- */

    onChange(event) {
      const picker = event.target.closest('variant-picker');
      if (!picker) return;

      // Combined listings: the value belongs to a sibling product — navigate like ProductInfo does on the PDP.
      const input = picker.getActualInput?.(event.target) ?? event.target;
      const productUrl = input?.getAttribute?.('data-product-url');
      if (productUrl) {
        window.location.href = productUrl;
        return;
      }

      // VariantPicker's own change listener (registered on the descendant) has already resolved
      // currentVariant when the picker has data-dynamic-variants + inline variant JSON (<= 250 variants).
      // null = the chosen combination does not exist as a variant.
      const variant = picker.currentVariant;
      if (variant) {
        this.onVariantChange(picker, variant);
      }
      else {
        this.setUnavailable(picker);
      }
    }

    onVariantChange(picker, variant) {
      const panel = picker.closest('[data-buy]');
      if (!panel) return;

      // Dropdown picker: VariantPicker only toggles the `selected` attribute on <option>, which the browser
      // ignores once the user has picked — sync the live value to the resolved (closest available) variant.
      picker.optionContainers.forEach((container, index) => {
        if (container.tagName !== 'SELECT') return;
        const value = variant[`option${index + 1}`];
        if (value != null && container.value !== value) container.value = value;
      });

      const form = panel.querySelector('form[is="product-form"]');
      const input = form?.querySelector('[data-variant-input]');
      if (input) input.value = variant.id;
      const quantity = form?.querySelector('[name="quantity"]');
      if (quantity) quantity.value = variant.quantity_rule?.min || 1;

      const price = panel.querySelector('[data-price]');
      price?.removeAttribute('hidden');

      this.updateOptionLabels(picker);
      this.updatePrice(price, variant);
      this.toggleButton(form, variant);
      this.updateCaption(this.lastFrame, variant);
      this.swapFrame(this.lastFrame, variant);

      this.dispatchEvent(new CustomEvent('scroll-story:variant', { bubbles: true, detail: { variant } }));
    }

    /**
     * The chosen option combination has no variant (ProductInfo shows "Unavailable" in the same case).
     */
    setUnavailable(picker) {
      const panel = picker.closest('[data-buy]');
      if (!panel) return;

      const form = panel.querySelector('form[is="product-form"]');
      form?.toggleSubmitButton?.(true, theme.variantStrings.unavailable, true);
      form?.querySelector('[type="submit"]')?.setAttribute('aria-label', theme.variantStrings.unavailable);
      panel.querySelector('[data-price]')?.setAttribute('hidden', '');
      this.updateOptionLabels(picker);
    }

    /**
     * "Color: Gold Tone" — the picker snippets render the selected value in <span id="…-option{n}">;
     * on the product page ProductInfo refreshes it through the Section Rendering API, here we do it in place.
     */
    updateOptionLabels(picker) {
      picker.optionContainers.forEach((container, index) => {
        const label = picker.querySelector(`[id$="-option${index + 1}"]`);
        if (!label) return;
        // option.value is the bare name; option.text carries the " - Sold out" suffix in the dropdown picker
        const value = container.tagName === 'SELECT'
          ? container.value
          : container.querySelector('input:checked')?.value;
        if (value != null) label.textContent = value.trim();
      });
    }

    get moneyFormat() {
      return theme.settings.currencyCodeEnabled ? theme.settings.moneyWithCurrencyFormat : theme.settings.moneyFormat;
    }

    formatMoney(cents) {
      return theme.Currency.formatMoney(cents, this.moneyFormat);
    }

    /**
     * Money formats are HTML in this theme (theme.Currency.formatMoney output is written with innerHTML
     * everywhere), so the caption is an HTML string with the variant title escaped.
     */
    /**
     * Step layers show the price only; the buy layer (last frame) also names the variant — same as the Liquid render.
     */
    captionDetail(frame, variant) {
      const price = this.formatMoney(variant.price);
      const withVariant = frame.hasAttribute('data-last-frame') && frame.hasAttribute('data-has-options');
      return withVariant ? `${escapeHtml(variant.title)} · ${price}` : price;
    }

    updateCaption(frame, variant) {
      const caption = frame?.querySelector('.scroll-story__caption');
      if (!caption || caption.hasAttribute('data-custom-detail')) return;   // merchant wrote the detail text
      const detail = caption.querySelector('[data-caption-detail]');
      if (detail) detail.innerHTML = this.captionDetail(frame, variant);
    }

    /**
     * Rewrites the product-price snippet output for a variant (regular + sale price, sr-only labels).
     * The panel renders the snippet with hide_unit_price, so the only sr-only children are the sale labels.
     */
    updatePrice(wrapper, variant) {
      const price = wrapper?.querySelector('.price');
      if (!price) return;

      const regular = price.querySelector('.price__regular');
      if (regular) regular.innerHTML = this.formatMoney(variant.price);

      const onSale = variant.compare_at_price != null && variant.compare_at_price > variant.price;
      price.classList.toggle('price--on-sale', onSale);
      price.querySelectorAll(':scope > .sr-only').forEach((el) => {
        if (!el.nextElementSibling?.classList.contains('unit-price')) el.remove();
      });
      price.querySelectorAll(':scope > .price__sale').forEach((el) => el.remove());

      if (onSale && regular) {
        const saleLabel = document.createElement('span');
        saleLabel.className = 'sr-only';
        saleLabel.textContent = wrapper.dataset.salePriceLabel || '';

        const regularLabel = document.createElement('span');
        regularLabel.className = 'sr-only';
        regularLabel.textContent = wrapper.dataset.regularPriceLabel || '';

        const sale = document.createElement('span');
        sale.className = 'price__sale inline-flex items-center h-auto relative';
        sale.innerHTML = this.formatMoney(variant.compare_at_price);

        price.prepend(saleLabel);
        regular.after(regularLabel, sale);
      }
    }

    /**
     * ProductForm.toggleSubmitButton releases its sold-out latch and sets disabled + "Sold out" / "Add to cart";
     * the merchant's own label and the accessible name are restored afterwards.
     */
    toggleButton(form, variant) {
      const button = form?.querySelector('[type="submit"]');
      if (!button) return;

      form.toggleSubmitButton?.(!variant.available, theme.variantStrings.soldOut);

      const text = button.querySelector('.btn-text > span');
      if (variant.available) {
        if (text?.dataset.label) text.textContent = text.dataset.label;
        if (button.dataset.ariaLabel) button.setAttribute('aria-label', button.dataset.ariaLabel);
      }
      else {
        button.setAttribute('aria-label', theme.variantStrings.soldOut);
      }
    }

    /**
     * Decode-then-swap the variant image into a media layer (keeps a responsive srcset like
     * ProductStickyForm.generateSrcset); skipped when the merchant assigned a custom image.
     * A variant without its own media falls back to the product image (data-fallback-* on the frame).
     */
    swapFrame(frame, variant) {
      if (!frame || frame.hasAttribute('data-custom-image')) return;

      const img = frame.querySelector('img');
      let image = variant.featured_media?.preview_image;
      let alt = variant.featured_media?.alt;
      if (!image && frame.dataset.fallbackSrc) {
        image = { src: frame.dataset.fallbackSrc, width: parseInt(frame.dataset.fallbackWidth, 10) || 2000 };
        alt = frame.dataset.fallbackAlt;
      }
      if (!image || !img) return;

      const widths = [300, 400, 500, 600, 700, 800, 900, 1000, 1200, 1400, 1600, 1800].filter((width) => width <= image.width);
      const join = image.src.includes('?') ? '&' : '?';
      const srcset = widths.map((width) => `${image.src}${join}width=${width} ${width}w`).join(', ');
      const src = `${image.src}${join}width=${widths[widths.length - 1] || image.width}`;
      if (frame.dataset.pendingSrc === src) return;
      frame.dataset.pendingSrc = src;

      const probe = new Image();
      probe.sizes = img.sizes;
      probe.srcset = srcset;
      probe.src = src;

      const ready = probe.decode ? probe.decode().catch(() => {}) : Promise.resolve();
      ready.then(() => {
        if (frame.dataset.pendingSrc !== src || !frame.isConnected) return;

        img.srcset = srcset;
        img.src = src;
        if (alt) img.alt = alt;
        // Mobile layers size themselves by the image ratio (see the CSS); keep it in sync with the new image.
        if (probe.naturalWidth && probe.naturalHeight) {
          frame.style.setProperty('--scroll-story-ar', (probe.naturalWidth / probe.naturalHeight).toFixed(4));
        }
      });
    }
  }

  /**
   * <shop-the-story>: one product per step, "Add the set" panel.
   * One state per product: step picker ⇄ panel <select> ⇄ price ⇄ caption ⇄ hidden id ⇄ totals.
   */
  class ShopTheStory extends ScrollStory {
    init() {
      if (this.initialized) return;
      super.init();
      if (!this.media || this.steps.length === 0) return;

      this.on(this, 'click', this.onClick.bind(this));
      this.updateTotals();
    }

    get panel() {
      return this.querySelector('[data-buy]');
    }

    onChange(event) {
      const select = event.target.closest('select[data-set-variant]');
      if (!select) return super.onChange(event);

      const productId = select.closest('[data-row]')?.dataset.product;
      const stepPicker = this.querySelector(`.scroll-story__step-buy[data-product="${productId}"] variant-picker`);
      const variant = stepPicker?.getVariantData?.().find((item) => String(item.id) === select.value);

      if (stepPicker && variant) {
        // Let VariantPicker own availability: set every option container, then re-dispatch change on the last one.
        const last = this.syncPicker(stepPicker, variant);
        if (last) {
          this.sourceSelect = select;
          last.dispatchEvent(new Event('change', { bubbles: true }));
          this.sourceSelect = null;
          return;
        }
      }

      // No step picker (show_step_add off, or the step has no inline variant JSON): use the <option> data.
      this.applyVariant(productId, this.variantFromOption(select.selectedOptions[0]), select);
    }

    variantFromOption(option) {
      if (!option) return null;
      const data = option.dataset;
      return {
        id: option.value,
        title: data.variantTitle || option.text.trim(),
        price: +data.variantPrice || 0,
        compare_at_price: data.variantCompareAtPrice ? +data.variantCompareAtPrice : null,
        available: !option.disabled,
        quantity_rule: { min: +data.variantMin || 1 },
        featured_media: data.variantImage ? { preview_image: { src: data.variantImage, width: +data.variantImageWidth || 2000 } } : null
      };
    }

    /**
     * Writes a variant into a picker's option containers without dispatching change. Returns the last input touched.
     */
    syncPicker(picker, variant) {
      let last = null;
      picker.optionContainers.forEach((container, index) => {
        const value = variant[`option${index + 1}`];
        if (value == null) return;
        if (container.tagName === 'SELECT') {
          container.value = value;
          last = container;
        }
        else {
          const radio = container.querySelector(`input[value="${CSS.escape(value)}"]`);
          if (radio) {
            radio.checked = true;
            last = radio;
          }
        }
      });
      return last;
    }

    onVariantChange(picker, variant) {
      const step = picker.closest('.scroll-story__step-buy');
      if (!step) return super.onVariantChange(picker, variant);

      this.applyVariant(step.dataset.product, variant, picker);
    }

    setUnavailable(picker) {
      const step = picker.closest('.scroll-story__step-buy');
      if (!step) return super.setUnavailable(picker);

      const form = step.querySelector('form[is="product-form"]');
      form?.toggleSubmitButton?.(true, theme.variantStrings.unavailable, true);
      form?.querySelector('[type="submit"]')?.setAttribute('aria-label', theme.variantStrings.unavailable);
      step.querySelector('[data-price]')?.setAttribute('hidden', '');
      this.updateOptionLabels(picker);
    }

    /**
     * Single writer for a product's state (a product may appear in several steps; the panel has one row per product).
     */
    applyVariant(productId, variant, source) {
      if (!productId || !variant) return;

      const row = this.querySelector(`[data-row][data-product="${productId}"]`);
      const select = row?.querySelector('select[data-set-variant]');
      if (select && select !== source && select !== this.sourceSelect && select.value !== String(variant.id)) {
        select.value = variant.id;   // programmatic value: no change event, no loop
      }
      if (row) {
        const rowPrice = row.querySelector('[data-row-price]');
        if (rowPrice) rowPrice.innerHTML = this.formatMoney(variant.price);
        this.swapThumb(row, variant);
      }

      // Every step of this product (duplicates included), with or without the per-step controls.
      this.querySelectorAll(`[data-step][data-product="${productId}"]`).forEach((step) => {
        const controls = step.querySelector('.scroll-story__step-buy');
        if (controls) {
          const picker = controls.querySelector('variant-picker');
          if (picker) {
            if (picker === source) {
              // Dropdown picker: VariantPicker may have resolved the closest available variant (sold-out choice);
              // the live <select> value must follow it (radios are already re-checked by setInputStates).
              picker.optionContainers.forEach((container, index) => {
                const value = variant[`option${index + 1}`];
                if (container.tagName === 'SELECT' && value != null && container.value !== value) {
                  container.value = value;
                  picker.markSelectedOption?.(container);
                }
              });
            }
            else {
              // Duplicate step of the same product: write the inputs AND push VariantPicker's own state
              // (no change dispatch — a real event would re-enter applyVariant).
              const last = this.syncPicker(picker, variant);
              if (last) {
                picker.currentVariant = variant;
                picker.currentOptions = picker.optionContainers.map((container) => (
                  container.tagName === 'SELECT' ? container.value : container.querySelector('input:checked')?.value
                ));
                picker.optionContainers.forEach((container) => {
                  if (container.tagName === 'SELECT') picker.markSelectedOption?.(container);
                });
                picker.updateAvailableOptions?.(last);
              }
            }
            this.updateOptionLabels(picker);
          }

          const form = controls.querySelector('form[is="product-form"]');
          const input = form?.querySelector('[data-variant-input]');
          if (input) input.value = variant.id;
          const quantity = form?.querySelector('[name="quantity"]');
          if (quantity) quantity.value = variant.quantity_rule?.min || 1;

          const price = controls.querySelector('[data-price]');
          price?.removeAttribute('hidden');
          this.updatePrice(price, variant);
          this.toggleButton(form, variant);
        }

        const frame = this.frames.find((item) => item.dataset.stepId === step.dataset.stepId);
        if (frame) {
          this.updateCaption(frame, variant);
          this.swapFrame(frame, variant);
        }
      });

      this.updateTotals();
      this.dispatchEvent(new CustomEvent('scroll-story:variant', { bubbles: true, detail: { productId, variant } }));
    }

    /**
     * Row thumbnail follows the selected variant (decode-then-swap, 88/176 widths like the Liquid render);
     * a variant without media falls back to the product image (data-fallback-* on the row).
     */
    swapThumb(row, variant) {
      const img = row.querySelector('.scroll-story__thumb img');
      if (!img) return;

      let image = variant.featured_media?.preview_image;
      if (!image && row.dataset.fallbackSrc) {
        image = { src: row.dataset.fallbackSrc, width: parseInt(row.dataset.fallbackWidth, 10) || 2000 };
      }
      if (!image) return;

      const join = image.src.includes('?') ? '&' : '?';
      const widths = [88, 176].filter((width) => width <= image.width);
      const srcset = widths.map((width) => `${image.src}${join}width=${width} ${width}w`).join(', ');
      const src = `${image.src}${join}width=${widths[widths.length - 1] || image.width}`;
      if (row.dataset.pendingSrc === src) return;
      row.dataset.pendingSrc = src;

      const probe = new Image();
      probe.sizes = img.sizes;
      probe.srcset = srcset;
      probe.src = src;

      const ready = probe.decode ? probe.decode().catch(() => {}) : Promise.resolve();
      ready.then(() => {
        if (row.dataset.pendingSrc !== src || !row.isConnected) return;
        img.srcset = srcset;
        img.src = src;
      });
    }

    /**
     * Set total = Σ selected prices; the discounted total is a client-side preview only (see the discount setting info).
     */
    updateTotals() {
      const panel = this.panel;
      if (!panel) return;

      const fields = Array.from(panel.querySelectorAll('[data-set-variant]'));
      if (!fields.length) return;

      const items = fields.map((field) => (field.tagName === 'SELECT' ? field.selectedOptions[0] : field)).filter(Boolean);
      const full = items.reduce((sum, item) => sum + (+item.dataset.variantPrice || 0), 0);
      const percent = +this.dataset.discount || 0;
      const fullElement = panel.querySelector('[data-total-full]');
      const showDiscount = percent > 0 && fullElement && !fullElement.hidden;

      const total = panel.querySelector('[data-total]');
      // Integer cents, round half-up — same arithmetic as the Liquid render (times keep_percent | plus: 50 | divided_by: 100).
      if (total) total.innerHTML = this.formatMoney(showDiscount ? Math.round((full * (100 - percent)) / 100) : full);
      const fullAmount = fullElement?.querySelector('[data-total-full-amount]');
      if (fullAmount) fullAmount.innerHTML = this.formatMoney(full);

      const unavailable = items.some((item) => item.disabled || item.dataset.available === 'false');
      const button = panel.querySelector('[data-add-set]');
      if (!button) return;
      button.toggleAttribute('aria-disabled', unavailable);
      const text = button.querySelector('.btn-text > span');
      if (text) text.textContent = unavailable ? text.dataset.soldOut : text.dataset.label;
    }

    get setItems() {
      const panel = this.panel;
      if (!panel) return [];
      return Array.from(panel.querySelectorAll('[data-set-variant]')).map((field) => {
        const item = field.tagName === 'SELECT' ? field.selectedOptions[0] : field;
        return { id: field.value, quantity: +item?.dataset.variantMin || 1 };
      }).filter((item) => item.id);
    }

    onClick(event) {
      const button = event.target.closest('[data-add-set]');
      if (!button || button.hasAttribute('aria-disabled')) return;

      const items = this.setItems;
      if (items.length) this.addItems(items, button);
    }

    /**
     * Multi-item add — same recipe as CompactProductBundle.onSubmitHandler (assets/product-bundle.js).
     */
    addItems(items, button) {
      const pageCart = document.body.classList.contains('template-cart') || theme.settings.cartType === 'page';

      // Cart page mode without a discount code: plain form POST like the bundle sections
      // (never inside the theme editor, where a navigation would leave the preview).
      if (pageCart && !this.dataset.discountCode && !window.Shopify?.designMode) {
        theme.utils.postLink2(theme.routes.cart_add_url, { parameters: { items } });
        return;
      }

      if (button.hasAttribute('aria-busy')) return;
      this.error = false;
      this.activeElement = button;
      this.handleErrorMessage();

      const sectionsToBundle = [];
      document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } }));

      const body = JSON.stringify({ items, sections: sectionsToBundle, sections_url: window.location.pathname });

      button.setAttribute('aria-disabled', 'true');
      button.setAttribute('aria-busy', 'true');

      fetch(theme.routes.cart_add_url, { ...theme.utils.fetchConfig('javascript'), body })
        .then((response) => response.json())
        .then(async (parsedState) => {
          if (parsedState.status) {
            this.handleErrorMessage(parsedState.description);
            document.dispatchEvent(new CustomEvent('ajaxProduct:error', { detail: { errorMessage: parsedState.description } }));
            // Sold-out latch, same as CompactProductBundle: label + aria-disabled stay until a variant changes.
            const text = button.querySelector('.btn-text > span');
            if (text?.dataset.soldOut) text.textContent = text.dataset.soldOut;
            this.error = true;
            return;
          }

          let cartJson = await this.applyDiscountCode(sectionsToBundle);
          if (!cartJson) {
            cartJson = await (await fetch(theme.routes.cart_url, { ...theme.utils.fetchConfig('json', 'GET') })).json();
            cartJson['sections'] = parsedState['sections'];
          }

          if (pageCart && !window.Shopify?.designMode) {
            window.location.href = theme.routes.cart_url;
            return;
          }

          theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'shop-the-story', cart: cartJson });
          (parsedState.items || [parsedState]).forEach((product) => {
            document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { product } }));
          });

          document.querySelector('cart-drawer')?.show(this.activeElement);
        })
        .catch((error) => {
          console.error(error);
        })
        .finally(() => {
          button.removeAttribute('aria-busy');
          if (!this.error) button.removeAttribute('aria-disabled');
        });
    }

    /**
     * Optional discount code (section setting) — CartDiscount recipe (assets/cart.js): merge with the codes already
     * on the cart, apply through /cart/update.js, keep the result only when Shopify reports the code as applicable.
     */
    async applyDiscountCode(sections) {
      const code = this.dataset.discountCode;
      if (!code) return null;

      try {
        const current = await (await fetch(theme.routes.cart_url, { ...theme.utils.fetchConfig('json', 'GET') })).json();
        const existing = (current.discount_codes || []).map((discount) => discount.code);
        if (existing.includes(code)) return null;

        const body = JSON.stringify({ discount: [...existing, code].join(','), sections });
        const cart = await (await fetch(theme.routes.cart_update_url, { ...theme.utils.fetchConfig('json'), body })).json();
        const applied = (cart.discount_codes || []).some((discount) => discount.code === code && discount.applicable);
        if (applied) return cart;

        // Shopify keeps a non-applicable code on the cart: restore the previous codes so the drawer stays clean.
        await fetch(theme.routes.cart_update_url, { ...theme.utils.fetchConfig('json'), body: JSON.stringify({ discount: existing.join(',') }) });
        return null;
      }
      catch (error) {
        console.error(error);
        return null;
      }
    }

    handleErrorMessage(errorMessage = false) {
      const element = this.panel?.querySelector('.product-form__error-message');
      if (!element) return;

      element.toggleAttribute('hidden', !errorMessage);
      element.innerText = errorMessage || '';
    }
  }

  customElements.define('scroll-story', ScrollStory);
  if (!customElements.get('shop-the-story')) customElements.define('shop-the-story', ShopTheStory);
}
